import React, { useState, useEffect } from "react";
import axios from "axios";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { ToastContainer, toast } from "react-toastify"; // Import toast
import "react-toastify/dist/ReactToastify.css"; // Import toast styles
import SettingsIcon from "@mui/icons-material/Settings";
import { useDrop, useDrag } from "react-dnd";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { getToken, base_url } from "../../config/config";

const labelToColorMap = {
  "Materialeingang / Materialausgang mit Kontrolle und Weitergabe an die Produktion incl. Auftragsnummer":
    "#005B96",
  "Baugruppe montieren": "orange",
  "Maschine einrichten": "teal",
  "CNC Fräsen": "#1FD655",
  "Konventionell Fräsen": "#649200",
  "CNC Drehen": "#A2AFB0",
  "Konventionell Drehen": "grey",
  Schweißen: "#95CBE2",
  Sägen: "#844204",
  "Konservieren / Lackieren": "#680000",
  "Verpacken / Versenden": "#E9D700",
  "Transport innerhalb der Firma": "#1E64AF",
  Qualitätskontrolle: "green",
  Sonstiges: "#557882",
  "Angaben Formulare zu re übergeben": "pink",
  Stoßen: "#60CDB8",
  "Normalisieren / Härten": "#373737",
  Löten: "#373737", // Assuming same color as id 17
  Unterbrechung: "#FFC107", // Special case for breaks
};

const NEW_PROCESS = "NEW_PROCESS";
const monthNames = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const dayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

const MainPlanning = () => {
  const [planningProcesses, setPlanningProcesses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showAttributeMenu, setShowAttributeMenu] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState({
    showANR: false,
    showHours: false,
    showNames: false,
    showAuftragsbezeichnung: false,
    showLabel: false,
  });
  const [attendanceData, setAttendanceData] = useState([]); // State for attendance data
  const [searchQuery, setSearchQuery] = useState(""); // State for search query

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [machineData, setMachineData] = useState([]);

  const [selectedProcess, setSelectedProcess] = useState(null);
  const [newProcess, setNewProcess] = useState({
    anr: "",
    label: "",
    auftragsbezeichnung: "",
    hours: "",
    machinename: "",
    names: [],
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);

  // ... existing functions and useEffects ...
  const [selectedProcessIds, setSelectedProcessIds] = useState([]);

  // Function to toggle process selection
  const toggleProcessSelection = (processId) => {
    setSelectedProcessIds((prevSelected) => {
      if (prevSelected.includes(processId)) {
        return prevSelected.filter((id) => id !== processId);
      } else {
        return [...prevSelected, processId];
      }
    });
  };

  const handleProcessClick = (process) => {
    setSelectedProcess(process);
    openModal();
  };

  const handleDeleteProcess = () => {
    if (selectedProcess && selectedProcess._id) {
      setProcessToDelete(selectedProcess._id);
      setIsDeleteModalOpen(true);
    } else {
      toast.error("Process ID not found.");
    }
  };

  const confirmDeleteProcess = async () => {
    if (!processToDelete) {
      toast.error("No process selected for deletion.");
      return;
    }

    try {
      const response = await axios.delete(
        `${base_url}/v1/DeleteProjectPlanning/${processToDelete}`,
      );

      if (response.status === 200) {
        // Remove the deleted process from the state
        setPlanningProcesses((prevProcesses) =>
          prevProcesses.filter((process) => process._id !== processToDelete),
        );
        toast.success("Process deleted successfully!");
      } else {
        toast.error("Failed to delete the process.");
      }
    } catch (error) {
      console.error("Error deleting process:", error);
      toast.error("An error occurred while deleting the process.");
    } finally {
      setIsDeleteModalOpen(false);
      setProcessToDelete(null);
      closeModal(); // Close the edit modal if it's open
    }
  };

  useEffect(() => {
    console.log("Updated selectedProcess:", selectedProcess);
  }, [selectedProcess]);

  // State to hold the process being edited
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProcess(null); // Reset selectedProcess
    setNewProcess({
      anr: "",
      label: "",
      auftragsbezeichnung: "",
      hours: "",
      machinename: "",
      names: [],
    }); // Reset newProcess
  };

  const handleEditProcessChange = (key, value) => {
    setSelectedProcess((prevProcess) => ({
      ...prevProcess,
      [key]: value,
    }));
  };

  const handleCreateProcess = () => {
    if (
      newProcess.label &&
      newProcess.anr &&
      newProcess.names &&
      newProcess.machinename
    ) {
      // Check if a process with the same ANR, names, machinename, and date already exists
      const processExists = planningProcesses.some(
        (process) =>
          process.anr === newProcess.anr &&
          process.names.join(",") === newProcess.names.join(",") &&
          process.machinename === newProcess.machinename &&
          process.date?.toDateString() === newProcess.date?.toDateString(),
      );

      if (!processExists) {
        setPlanningProcesses((prevProcesses) => [
          ...prevProcesses,
          { ...newProcess, id: new Date().getTime(), date: null },
        ]);
        closeModal();
        toast.success(
          "New process created! Drag it to the calendar to schedule.",
        );
        setNewProcess({});
      } else {
        toast.error(
          "A process with the same ANR, employee names, and machine on the same date already exists.",
        );
      }
    } else {
      toast.error("Please fill in all required fields.");
    }
  };

  const handleSaveEditedProcess = async () => {
    console.log("selectedProcess");
    if (!selectedProcess) return;

    try {
      const response = await axios.put(
        `${base_url}/v1/UpdateProjectPlanning/${selectedProcess._id}`,
        selectedProcess,
      );

      if (response.status === 200) {
        const updatedProcess = response.data;

        // Update the process in the local state
        setPlanningProcesses((prevProcesses) =>
          prevProcesses.map((process) =>
            process._id === updatedProcess._id
              ? { ...updatedProcess, date: new Date(updatedProcess.date) } // Update the specific process
              : process,
          ),
        );

        toast.success("Process updated successfully!");
        closeModal();
      } else {
        toast.error("Failed to update the process.");
      }
    } catch (error) {
      console.error("Error updating process:", error);
      toast.error("An error occurred while updating. Please try again.");
    }
  };

  // Inside the JSX, update the Dialog component to show selected process details for editing

  useEffect(() => {
    const fetchMachineData = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getAllMachines`);
        const data = await response.json();
        setMachineData(data);
      } catch (error) {
        console.error("Error fetching machine data:", error);
      }
    };
    fetchMachineData();
  }, []);

  const handleMachineChange = (event) => {
    const selectedMachineName = event.target.value;

    // Find the selected machine from the machine data
    const selectedMachine = machineData.find(
      (machine) => machine.name === selectedMachineName,
    );

    if (selectedProcess) {
      // Editing an existing process
      if (
        selectedMachine &&
        selectedMachine.skills &&
        selectedMachine.skills.length > 0
      ) {
        setSelectedProcess((prev) => ({
          ...prev,
          machinename: selectedMachineName,
          label: selectedMachine.skills[0], // Set the first skill as the label
        }));
      } else {
        setSelectedProcess((prev) => ({
          ...prev,
          machinename: selectedMachineName,
          label: "",
        }));
      }
    } else {
      // Creating a new process
      if (
        selectedMachine &&
        selectedMachine.skills &&
        selectedMachine.skills.length > 0
      ) {
        setNewProcess((prev) => ({
          ...prev,
          machinename: selectedMachineName,
          label: selectedMachine.skills[0], // Set the first skill as the label
        }));
      } else {
        setNewProcess((prev) => ({
          ...prev,
          machinename: selectedMachineName,
          label: "",
        }));
      }
    }

    console.log(`Machine changed to: ${selectedMachineName}`);
  };

  const handleEmployeeChange = (event) => {
    setNewProcess({ ...newProcess, names: event.target.value });
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllEmployees`);
        setEmployees(response.data);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchPlanningData = async () => {
      try {
        const response = await fetch(`${base_url}/v1/GetAllProjectPlanning`);
        const data = await response.json();
        const allProcesses = data.flatMap((project) =>
          project.data.map((item) => ({
            ...item,
            projectId: project._id, // Associate the main project ID here
            date: new Date(item.date),
          })),
        );
        setPlanningProcesses(allProcesses);
      } catch (error) {
        console.error("Error fetching planning data:", error);
      }
    };

    const fetchAttendanceData = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getDailyAttendance`);
        const data = await response.json();
        setAttendanceData(data.data);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    };

    fetchPlanningData();

    fetchAttendanceData();
  }, []);

  const totalDaysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // Find out the day of the week of the 1st of the month
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  // Calculate the number of preceding days needed to align the month to start on Sunday
  const precedingDays = firstDayOfMonth === 0 ? 0 : firstDayOfMonth;

  // Create days array to ensure the month starts on Sunday
  const days = [
    ...Array(precedingDays).fill(null), // Fill with null for days before the 1st
    ...[...Array(totalDaysInMonth)].map((_, i) => {
      const date = new Date(currentYear, currentMonth, i + 1);
      return { date, dayNumber: i + 1 };
    }),
  ];

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prevYear) => prevYear - 1);
    } else {
      setCurrentMonth((prevMonth) => prevMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prevYear) => prevYear + 1);
    } else {
      setCurrentMonth((prevMonth) => prevMonth + 1);
    }
  };

  const exportToPDF = () => {
    const input = document.getElementById("calendar-container");
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("l", "mm", "a4");
      const imgWidth = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pageHeight = 210;
      let heightLeft = imgHeight;

      pdf.setFontSize(18);
      pdf.text(`Projektplanung`, 10, 20);
      pdf.setFontSize(12);

      let position = 30;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        pdf.addPage();
        pdf.text(`Projektplanung`, 10, 20);
        pdf.setFontSize(12);
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Projektplanung.pdf`);
    });
  };

  const getAbsentUsersOnDate = (date) => {
    const formattedDate = date.toLocaleDateString("en-CA");
    return attendanceData
      .filter(
        (attendance) =>
          attendance.Date === formattedDate && attendance.Abwesend,
      )
      .map((attendance) => attendance.user);
  };

  const handleAttributeToggle = (attribute) => {
    setSelectedAttributes((prevState) => ({
      ...prevState,
      [attribute]: !prevState[attribute],
    }));
  };

  // const handleSave = async () => {
  //     try {
  //         const updatedProcesses = planningProcesses.map(process => ({
  //             id: process.id,
  //             anr: process.anr,
  //             auftragsbezeichnung: process.auftragsbezeichnung,
  //             hours: process.hours,
  //             names: process.names,
  //             machinename: process.machinename,
  //             date: process.date,
  //         }));

  //         const response = await axios.post('${base_url}/v1/CreateProjectPlanning', {
  //             processes: updatedProcesses,
  //         });

  //         if (response.status === 201) {
  //             toast.success('Saved successfully!');
  //         } else {
  //             toast.error('Failed to save calendar processes.');
  //         }
  //     } catch (error) {
  //         console.error('Error saving calendar processes:', error);
  //         toast.error('An error occurred while saving. Please try again.');
  //     }
  // };

  const handleANRChange = async (e) => {
    const anr = e.target.value;
    setNewProcess({ ...newProcess, anr }); // Update ANR value

    if (anr.length > 2) {
      // Trigger the API call if the ANR length is more than 2 characters
      try {
        const response = await axios.post(`${base_url}/v1/getProjectbyANR`, {
          ANR: anr,
        });

        if (response.data.status === 1 && response.data.data.length > 0) {
          const project = response.data.data[0];
          setNewProcess((prevProcess) => ({
            ...prevProcess,
            auftragsbezeichnung: project.Auftragsbezeichnung,
          }));
          toast.success("Auftragsbezeichnung auto-filled successfully!");
        } else {
          // toast.info('No project found for this ANR.');
          setNewProcess((prevProcess) => ({
            ...prevProcess,
            auftragsbezeichnung: "",
          }));
        }
      } catch (error) {
        console.error("Error fetching project by ANR:", error);
        toast.error(
          "Failed to retrieve Auftragsbezeichnung. Please try again.",
        );
      }
    } else {
      setNewProcess((prevProcess) => ({
        ...prevProcess,
        auftragsbezeichnung: "",
      }));
    }
  };

  const handleSave = async () => {
    try {
      // Filter out processes that are not yet saved (no backend ID)
      const newProcesses = planningProcesses.filter((process) => !process._id);

      // Ensure only unique new processes are sent for saving
      const uniqueNewProcesses = newProcesses.reduce((acc, process) => {
        const exists = acc.find(
          (p) =>
            p.anr === process.anr &&
            p.names.join(",") === process.names.join(",") &&
            p.machinename === process.machinename &&
            p.date?.toDateString() === process.date?.toDateString(),
        );
        if (!exists) acc.push(process);
        return acc;
      }, []);

      if (uniqueNewProcesses.length > 0) {
        const response = await axios.post(
          `${base_url}/v1/CreateProjectPlanning`,
          {
            processes: uniqueNewProcesses,
          },
        );

        if (response.status === 201) {
          toast.success("Saved successfully!");

          // Update the planningProcesses with the IDs returned from the backend to mark them as saved
          const savedProcesses = response.data.processes.map(
            (savedProcess) => ({
              ...savedProcess,
              date: new Date(savedProcess.date), // Parse the date correctly
            }),
          );

          setPlanningProcesses((prevProcesses) => [
            ...prevProcesses.filter((process) => process._id), // Keep already saved processes
            ...savedProcesses, // Add newly saved processes with backend IDs
          ]);
        } else {
          toast.error("Failed to save calendar processes.");
        }
      } else {
        toast.info("No new processes to save.");
      }
    } catch (error) {
      console.error("Error saving calendar processes:", error);
      toast.error("An error occurred while saving. Please try again.");
    }
  };

  const DraggableProcess = ({ process }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
      type: NEW_PROCESS,
      item: { ...process },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }));

    const handleDuplicate = () => {
      const duplicatedProcess = {
        ...process,
        id: new Date().getTime(), // Assign a new unique ID
        date: null, // Set the duplicated process date to null to allow dragging to a new date
      };
      setPlanningProcesses((prevProcesses) => [
        ...prevProcesses,
        duplicatedProcess,
      ]);
      toast.success("Process duplicated! Drag it to the calendar to schedule.");
    };

    const backgroundColor = labelToColorMap[process.label] || "#007BFF"; // Default color if label not found
    return (
      <div
        ref={drag}
        style={{
          ...processStyle,
          opacity: isDragging ? 0.5 : 1,
          cursor: "move",
          backgroundColor,
        }}>
        {process.anr} - {process.label}
        <button onClick={handleDuplicate} style={duplicateButtonStyle}>
          Duplicate
        </button>
      </div>
    );
  };

  const duplicateButtonStyle = {
    marginLeft: "10px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "3px",
    cursor: "pointer",
    padding: "5px 8px",
    fontSize: "12px",
  };

  return (
    <div>
      <ToastContainer /> {/* Toast container for notifications */}
      {/* Floating Button */}
      <button
        style={floatingButtonStyle}
        onClick={() => setShowAttributeMenu(!showAttributeMenu)}>
        <SettingsIcon style={{ fontSize: 25 }} />
      </button>
      <button style={floatingButtonStyle} onClick={openModal}>
        Add New Process
      </button>
      <Dialog open={isModalOpen} onClose={closeModal} fullWidth maxWidth='sm'>
        <DialogTitle>
          {selectedProcess ? "Edit Process" : "Create New Process"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label='ANR'
            value={selectedProcess ? selectedProcess.anr : newProcess.anr || ""}
            onChange={handleANRChange}
            fullWidth
            margin='normal'
          />
          <TextField
            label='Label'
            value={
              selectedProcess ? selectedProcess.label : newProcess.label || ""
            }
            onChange={(e) =>
              selectedProcess
                ? setSelectedProcess({
                    ...selectedProcess,
                    label: e.target.value,
                  })
                : setNewProcess({ ...newProcess, label: e.target.value })
            }
            fullWidth
            margin='normal'
          />
          <TextField
            label='Auftragsbezeichnung'
            value={
              selectedProcess
                ? selectedProcess.auftragsbezeichnung
                : newProcess.auftragsbezeichnung || ""
            }
            onChange={(e) =>
              selectedProcess
                ? setSelectedProcess({
                    ...selectedProcess,
                    auftragsbezeichnung: e.target.value,
                  })
                : setNewProcess({
                    ...newProcess,
                    auftragsbezeichnung: e.target.value,
                  })
            }
            fullWidth
            margin='normal'
          />
          <TextField
            label='Hours'
            type='number'
            value={
              selectedProcess ? selectedProcess.hours : newProcess.hours || ""
            }
            onChange={(e) =>
              selectedProcess
                ? setSelectedProcess({
                    ...selectedProcess,
                    hours: e.target.value,
                  })
                : setNewProcess({ ...newProcess, hours: e.target.value })
            }
            fullWidth
            margin='normal'
          />
          <FormControl fullWidth margin='normal'>
            <InputLabel>Machine Name</InputLabel>
            <Select
              value={
                selectedProcess
                  ? selectedProcess.machinename
                  : newProcess.machinename || ""
              }
              onChange={handleMachineChange} // Use the consolidated handler
              label='Machine Name'>
              <MenuItem value='' disabled>
                Select Machine Name
              </MenuItem>
              {machineData.map((machine) => (
                <MenuItem key={machine._id} value={machine.name}>
                  {machine.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin='normal'>
            <InputLabel>Assign Employee(s)</InputLabel>
            <Select
              multiple
              value={
                selectedProcess ? selectedProcess.names : newProcess.names || []
              }
              onChange={(e) =>
                selectedProcess
                  ? setSelectedProcess({
                      ...selectedProcess,
                      names: e.target.value,
                    })
                  : setNewProcess({ ...newProcess, names: e.target.value })
              }
              label='Assign Employee(s)'
              renderValue={(selected) => selected.join(", ")}>
              <MenuItem value='' disabled>
                Select Employee(s)
              </MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee._id} value={employee.name}>
                  {employee.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color='secondary'>
            Cancel
          </Button>
          <Button
            onClick={
              selectedProcess ? handleSaveEditedProcess : handleCreateProcess
            }
            color='primary'
            variant='contained'>
            {selectedProcess ? "Save Changes" : "Create Process"}
          </Button>
          <Button
            onClick={handleDeleteProcess}
            color='error'
            variant='outlined'>
            Delete Process
          </Button>
        </DialogActions>
      </Dialog>
      {/* Deletion Confirmation Dialog */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        fullWidth
        maxWidth='xs'>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this process?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteModalOpen(false)} color='secondary'>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteProcess}
            color='error'
            variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Render the new processes for dragging */}
      <div style={newProcessListStyle}>
        {planningProcesses
          .filter((process) => process.date === null)
          .map((process) => (
            <DraggableProcess key={process.id} process={process} />
          ))}
      </div>
      {/* Attribute Selection Menu */}
      {showAttributeMenu && (
        <div style={menuStyle}>
          <label>
            <input
              type='checkbox'
              checked={selectedAttributes.showANR}
              onChange={() => handleAttributeToggle("showANR")}
            />
            Show ANR
          </label>
          <label>
            <input
              type='checkbox'
              checked={selectedAttributes.showHours}
              onChange={() => handleAttributeToggle("showHours")}
            />
            Show Hours
          </label>

          <label>
            <input
              type='checkbox'
              checked={selectedAttributes.showNames}
              onChange={() => handleAttributeToggle("showNames")}
            />
            Show Names
          </label>
          <label>
            <input
              type='checkbox'
              checked={selectedAttributes.showAuftragsbezeichnung}
              onChange={() => handleAttributeToggle("showAuftragsbezeichnung")}
            />
            Show Auftragsbezeichnung
          </label>
          <label>
            <input
              type='checkbox'
              checked={selectedAttributes.showLabel}
              onChange={() => handleAttributeToggle("showLabel")}
            />
            Show Process Name
          </label>
        </div>
      )}
      {/* Search Input */}
      <div style={{ marginBottom: "20px" }}>
        <input
          type='text'
          placeholder='Search ANR or Auftragsbezeichnung'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: "10px",
            width: "100%",
            maxWidth: "400px",
            borderRadius: "5px",
            border: "1px solid #ddd",
          }}
        />
      </div>
      {/* Navigation controls for month */}
      <div style={navigationStyle}>
        <button onClick={handlePrevMonth}>Vorheriger Monat</button>
        <span>
          {monthNames[currentMonth]} {currentYear}
        </span>
        <button onClick={handleNextMonth}>Nächster Monat</button>
      </div>
      {/* Add Export Button */}
      <button onClick={exportToPDF} style={exportButtonStyle}>
        Export to PDF
      </button>
      {/* Calendar grid */}
      <div id='calendar-container' style={calendarContainerStyle}>
        <div style={calendarStyle}>
          {dayNames.map((dayName, index) => (
            <div key={index} style={headerStyle}>
              {dayName}
            </div>
          ))}
          {days.map((day, index) => {
            if (!day) {
              return <div key={index} style={dayStyle}></div>; // Render an empty cell for null days
            }
            return (
              <CalendarDay
                key={day.dayNumber}
                date={day.date}
                processes={planningProcesses.filter(
                  (process) =>
                    process.date &&
                    process.date.getDate() === day.date.getDate() &&
                    process.date.getMonth() === day.date.getMonth() &&
                    process.date.getFullYear() === day.date.getFullYear() &&
                    ((process.anr && process.anr.includes(searchQuery)) ||
                      (process.auftragsbezeichnung &&
                        process.auftragsbezeichnung.includes(searchQuery))),
                )}
                selectedAttributes={selectedAttributes}
                getAbsentUsersOnDate={getAbsentUsersOnDate}
                setPlanningProcesses={setPlanningProcesses}
                handleProcessClick={handleProcessClick}
                toggleProcessSelection={toggleProcessSelection} // Add this line
                selectedProcessIds={selectedProcessIds}
              />
            );
          })}

          <Button onClick={handleSave}>SAVE</Button>
        </div>
      </div>
    </div>
  );
};

// const DraggableProcess = ({ process }) => {
//     const [{ isDragging }, drag] = useDrag(() => ({
//         type: NEW_PROCESS,
//         item: { ...process },
//         collect: (monitor) => ({
//             isDragging: !!monitor.isDragging(),
//         }),
//     }));

//     return (
//         <div
//             ref={drag}
//             style={{
//                 ...processStyle,
//                 opacity: isDragging ? 0.5 : 1,
//                 cursor: 'move',
//                 backgroundColor: processColors[process.id] || '#007BFF',
//             }}
//         >
//             {process.anr} - {process.label}
//         </div>
//     );
// };

const DraggableCalendarProcess = ({
  process,
  selectedAttributes,
  toggleProcessSelection,
  selectedProcessIds,
  handleProcessClick,
}) => {
  const isSelected = selectedProcessIds.includes(process.id);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "CALENDAR_PROCESS",
      item: {
        processIds: isSelected ? selectedProcessIds : [process.id],
        fromDate: process.date,
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [process, selectedProcessIds],
  );

  const handleClick = (e) => {
    e.stopPropagation();
    toggleProcessSelection(process.id);
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    handleProcessClick(process);
  };

  const backgroundColor = isSelected
    ? "lightblue"
    : labelToColorMap[process.label] || "#007BFF";

  return (
    <div
      ref={drag}
      style={{
        ...processStyle,
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        backgroundColor,
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}>
      {/* Render process details */}
      <div style={{ fontSize: "16px", fontWeight: "bold" }}>{process.anr}</div>
      {selectedAttributes.showLabel && (
        <div style={{ marginTop: "5px" }}>Label: {process.label}</div>
      )}
      {/* Add other attributes as needed */}
    </div>
  );
};

const CalendarDay = ({
  date,
  processes,
  selectedAttributes,
  getAbsentUsersOnDate,
  setPlanningProcesses,
  handleProcessClick,
  toggleProcessSelection,
  selectedProcessIds,
}) => {
  const navigate = useNavigate();

  const [, drop] = useDrop({
    accept: [NEW_PROCESS, "CALENDAR_PROCESS"],
    drop: (item, monitor) => {
      if (monitor.getItemType() === NEW_PROCESS) {
        // Handle dropping new process onto calendar
        setPlanningProcesses((prevProcesses) =>
          prevProcesses.map((process) =>
            process.id === item.id ? { ...process, date } : process,
          ),
        );
        toast.success("Process scheduled on the calendar!");
      } else if (monitor.getItemType() === "CALENDAR_PROCESS") {
        // Handle dropping existing processes onto a new date
        const { processIds } = item;

        setPlanningProcesses((prevProcesses) =>
          prevProcesses.map((process) =>
            processIds.includes(process.id) ? { ...process, date } : process,
          ),
        );

        toast.success("Processes moved to new date!");
      }
    },
  });
  const [apiData, setApiData] = useState([]);

  useEffect(() => {
    // Fetch data from the API
    const fetchApiData = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getProjectUserByDate`);
        const data = await response.json();
        if (data.success) {
          setApiData(data.data); // Store the entire API data for later comparison
        }
      } catch (error) {
        console.error("Error fetching data from API:", error);
      }
    };

    fetchApiData();
  }, []);

  // Get the list of absent users for the given date
  const absentUsers = getAbsentUsersOnDate(date);

  // Check if there is at least one process where the assigned user is absent
  const isAnyAssignedUserAbsent = processes.some(
    (process) =>
      process.names && // Ensure names is defined
      process.names.some((name) => absentUsers.includes(name)), // Check if any assigned user is absent
  );

  // Filter out processes with id 0 (breaks)
  const filteredProcesses = processes.filter((process) => process.id !== 0);

  const isTileGreen = filteredProcesses.some((process) =>
    apiData.some(
      (item) => item.ANR === process.anr && item.Arbeiten === process.label,
    ),
  );
  // State for the hover popup
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [showReplacementPopup, setShowReplacementPopup] = useState(false);
  const [replacementCandidates, setReplacementCandidates] = useState([]);
  const [currentAbsentUser, setCurrentAbsentUser] = useState(null);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getAllEmployees`);
        const data = await response.json();
        setEmployees(data);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchEmployees();
  }, []);

  const handleMouseLeave = () => {
    setShowPopup(false);
    setPopupData(null);
  };

  const handleMouseEnter = (event, process) => {
    setPopupData(process);
    setShowPopup(true);
    setPopupPosition({ x: event.clientX, y: event.clientY });
  };

  const handleAbsentUserClick = (absentUser) => {
    const process = processes.find((proc) => proc.names?.includes(absentUser));

    if (!process) {
      console.error("No process found for the absent user:", absentUser);
      return;
    }

    setPopupData(process);

    const candidates = findReplacementCandidates(absentUser);
    setReplacementCandidates(candidates);
    setCurrentAbsentUser(absentUser);
    setShowReplacementPopup(true);
  };

  const handleReplaceUser = async (replacementUser) => {
    if (!popupData) {
      console.error("No popupData available to update the process.");
      return;
    }

    console.log("popupData:", popupData);
    console.log(
      "API URL:",
      `${base_url}/v1/UpdateProjectPlanning/${popupData._id}`,
    );

    try {
      const response = await fetch(
        `${base_url}/v1/UpdateProjectPlanning/${popupData._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ names: [replacementUser] }),
        },
      );

      if (!response.ok) {
        const errorResponse = await response.json();
        console.error("API error:", errorResponse);
        toast.error(
          `Fehler: ${errorResponse.message || "Unerwarteter Fehler"}`,
        );
        return;
      }

      const updatedProcess = await response.json();
      console.log("Updated process:", updatedProcess);

      // Update the local state to reflect the changes
      setPlanningProcesses((prevProcesses) =>
        prevProcesses.map((process) =>
          process._id === updatedProcess._id
            ? {
                ...process,
                ...updatedProcess,
                date: new Date(updatedProcess.date),
              } // Ensure date is parsed correctly
            : process,
        ),
      );

      toast.success(
        `${currentAbsentUser} wurde durch ${replacementUser} ersetzt.`,
      );
    } catch (error) {
      console.error("Network or parsing error:", error);
      toast.error("Fehler beim Aktualisieren des Prozesses.");
    }

    // Close the replacement popup
    setShowReplacementPopup(false);
  };

  const findReplacementCandidates = (absentUser) => {
    // Find the skills of the absent user
    const absentEmployee = employees.find((emp) => emp.name === absentUser);
    if (!absentEmployee || !absentEmployee.skills) return [];

    // Find other employees with the same skills
    return employees.filter(
      (emp) =>
        emp.name !== absentUser &&
        emp.skills?.some((skill) => absentEmployee.skills.includes(skill)), // Use optional chaining to ensure emp.skills is defined
    );
  };

  return (
    <div
      ref={drop}
      style={{
        ...dayStyle,
        backgroundColor: isAnyAssignedUserAbsent ? "red" : "#f9f9f9",
      }}>
      <strong>{date.toDateString().split(" ")[2]}</strong>
      {filteredProcesses.map((process, index) => (
        <DraggableCalendarProcess
          key={index}
          process={process}
          selectedAttributes={selectedAttributes}
          toggleProcessSelection={toggleProcessSelection}
          selectedProcessIds={selectedProcessIds}
          handleProcessClick={handleProcessClick} // Pass this prop
        />
      ))}
      {isAnyAssignedUserAbsent && (
        <div style={{ marginTop: "5px", color: "white", fontSize: "18px" }}>
          Abwesend:{" "}
          {processes
            .flatMap((process) => process.names || [])
            .filter((name) => absentUsers.includes(name))
            .map((user, index) => (
              <span
                key={index}
                onClick={() => handleAbsentUserClick(user)}
                style={{ cursor: "pointer", textDecoration: "underline" }}>
                {user}
              </span>
            ))}
        </div>
      )}

      {showReplacementPopup && (
        <div style={replacementPopupStyle}>
          <h4>Wählen Sie einen Ersatz für {currentAbsentUser}</h4>
          {replacementCandidates.length > 0 ? (
            replacementCandidates.map((candidate, index) => (
              <button
                key={index}
                onClick={() => handleReplaceUser(candidate.name)}
                style={candidateButtonStyle}>
                {candidate.name} - Skills: {candidate.skills.join(", ")}
              </button>
            ))
          ) : (
            <p>Keine passenden Kandidaten gefunden.</p>
          )}
          <button onClick={() => setShowReplacementPopup(false)}>
            Abbrechen
          </button>
        </div>
      )}

      {/* Hover Popup */}
      {showPopup && popupData && (
        <div
          style={{
            ...popupStyle,
            top: popupPosition.y + 10,
            left: popupPosition.x + 10,
          }}>
          <div>
            <strong>ANR:</strong> {popupData.anr}
          </div>
          <div>
            <strong>Auftragsbezeichnung:</strong>{" "}
            {popupData.auftragsbezeichnung}
          </div>
          <div>
            <strong>Prozess:</strong> {popupData.label}
          </div>
          <div>
            <strong>Hours:</strong> {popupData.hours}
          </div>
          <div>
            <strong>Machine Name:</strong> {popupData.machinename}
          </div>
          <div>
            <strong>Assigned to:</strong>{" "}
            {popupData.names ? popupData.names.join(", ") : "N/A"}
          </div>
        </div>
      )}
    </div>
  );
};

const floatingButtonStyle = {
  position: "fixed",
  bottom: "20px",
  right: "20px",
  padding: "15px 18px",
  backgroundColor: "black",
  color: "white",
  border: "none",
  borderRadius: "50%",
  cursor: "pointer",
  fontSize: "16px",
  boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
};

const menuStyle = {
  position: "fixed",
  bottom: "80px",
  right: "20px",
  backgroundColor: "white",
  padding: "10px",
  borderRadius: "5px",
  boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
  zIndex: 1000,
};

const calendarContainerStyle = {
  display: "flex",
  justifyContent: "center",
  padding: "20px",
};

const navigationStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "20px",
};

const calendarStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: "10px",
  width: "100%",
};

const headerStyle = {
  fontWeight: "bold",
  textAlign: "center",
  padding: "10px",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ddd",
};

const dayStyle = {
  textAlign: "center",
  padding: "10px",
  border: "1px solid #ddd",
  borderRadius: "5px",
  minHeight: "100px",
  backgroundColor: "#f9f9f9",
  boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
};

const processStyle = {
  padding: "10px",
  marginTop: "10px",
  borderRadius: "5px",
  border: "2px solid",
  backgroundColor: "#007BFF",
  color: "white",
  fontWeight: "bold",
  boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
};

const exportButtonStyle = {
  marginBottom: "10px",
  padding: "10px 20px",
  backgroundColor: "#007BFF",
  color: "white",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  fontSize: "16px",
};

// Styles for the popup
const popupStyle = {
  position: "absolute",
  backgroundColor: "#fff",
  padding: "10px",
  border: "1px solid #ddd",
  borderRadius: "5px",
  boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
  zIndex: 1000,
  whiteSpace: "nowrap",
};

const replacementPopupStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  padding: "20px",
  borderRadius: "5px",
  boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
  zIndex: 1000,
};

const candidateButtonStyle = {
  display: "block",
  width: "100%",
  padding: "10px",
  margin: "5px 0",
  backgroundColor: "#007BFF",
  color: "white",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "20px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "400px",
  },
};

const newProcessListStyle = {
  padding: "20px",
  marginBottom: "20px",
  border: "1px solid #ddd",
  borderRadius: "5px",
  backgroundColor: "#f9f9f9",
};

export default MainPlanning;
